<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetProductGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    product: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const productTypes = ref(null);
  const productGroups = ref(null);
  const taxCodes = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_3573211234170528_multiple = computed(() => false);

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    productTypes: productTypes.value,
    productGroups: productGroups.value,
    taxCodes: taxCodes.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_3573211234170528_multiple: b_3573211234170528_multiple.value,
  }));

  const $$load_productTypes = () => {
    return $fn.value.getEnumValuesFromSchema('ProductTypes');
  };
  const $$load_productGroups = () => {
    return $fn.value
      .fetch('list_group')
      .then((data) => data.data.map((p) => p.name));
  };
  const $$load_taxCodes = () => {
    return $fn.value
      .fetch('list_tax_code')
      .then((data) => data.map((p) => p.id));
  };
  const $$load_invoiceGroups = () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'productTypes' },
      { name: 'productGroups' },
      { name: 'taxCodes' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_3573211234170528_multiple', nodeId: '3573211234170528' },
    ],
    loaders: {
      productTypes: $$load_productTypes,
      productGroups: $$load_productGroups,
      taxCodes: $$load_taxCodes,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      labelAlign,
      productTypes,
      productGroups,
      taxCodes,
      invoiceGroups,
      ux,
      b_3573211234170528_multiple,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.product,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('product');
    }
  );

  const act_7315092382398562_reloadComponentData_kd3r2n3i9 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_izadfw7fw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_kd3r2n3i9,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_izadfw7fw],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_izadfw7fw,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_izadfw7fw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_izadfw7fw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="name"
      :label="pgettext('Product', 'Type')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormNativeSelect
        name="product_type"
        v-model="product.product_type"
        required
        :options="productTypes"
        autofocus
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="number"
      :label="pgettext('Product', 'Item number')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput name="number" v-model="product.number" required>
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="name"
      :label="pgettext('Product', 'Name')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput name="name" v-model="product.name" required> </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Product', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea name="description" v-model="product.description">
      </ItpFormTextarea>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="unit"
      :label="pgettext('Product', 'Unit')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput name="unit" v-model="product.unit" required> </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      :label-cols-md="4"
      name="sales_vat_code"
      :label="pgettext('Product', 'Sales tax code')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormNativeSelect
        name="sales_vat_code"
        v-model="product.sales_vat_code"
        :options="taxCodes"
        required
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="sales_vat_code"
      :label="pgettext('Product', 'Purchase tax code')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormNativeSelect
        name="purchase_vat_code"
        v-model="product.purchase_vat_code"
        :options="taxCodes"
        required
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="intermediated_service"
      :label="pgettext('Product', 'Intermediated service')"
      :labelAlign="labelAlign"
    >
      <ItpFormCheckbox
        name="intermediated_service"
        v-model="product.intermediated_service"
      >
      </ItpFormCheckbox>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="invoice_group_id"
      :label="pgettext('Product', 'Invoice Group')"
      :labelAlign="labelAlign"
    >
      <ItpFormSearchableSelect
        name="invoice_group_id"
        v-model="product.invoice_group_id"
        :multiple="b_3573211234170528_multiple"
        :options="invoiceGroups"
        valueField="id"
        textField="name"
        clearable
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <hr />
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="groups"
      :label="pgettext('Product', 'Groups')"
      :labelAlign="labelAlign"
    >
      <ItpFormSearchableSelect
        name="groups"
        v-model="product.groups"
        multiple
        :options="productGroups"
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="customs_tariff_no"
      :label="pgettext('Product', 'Customs tariff no.')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="customs_tariff_no"
        v-model="product.customs_tariff_no"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="hu_teszor_code"
      :label="pgettext('Product', 'TESZOR code (HU)')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="hu_teszor_code" v-model="product.hu_teszor_code">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="eu_ewc_code"
      :label="pgettext('Product', 'EWC code (EU)')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="eu_ewc_code" v-model="product.eu_ewc_code">
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="external_key"
      :label="pgettext('Product', 'External key')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput name="external_key" v-model="product.external_key">
      </ItpFormInput>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
